import { CheckCircle2, Eye, EyeOff, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useResetPassword } from "@/lib/api";
import { logger } from "@/lib/logger";

const PROGRAM_NAME = "Innovation Leadership Program";
const MIN_PASSWORD_LENGTH = 8;

/**
 * Supabase delivers the recovery credentials in the URL *hash* (never the
 * query string), e.g. `#access_token=…&type=recovery&…`. Reading it here keeps
 * the token out of the query string and out of any server logs.
 */
function readRecoveryToken(): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  const hash = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.hash;
  const params = new URLSearchParams(hash);
  const type = params.get("type");
  const token = params.get("access_token");
  if (token && (type === "recovery" || type === null)) {
    return token;
  }
  return null;
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const resetMutation = useResetPassword();
  const accessToken = useMemo(() => readRecoveryToken(), []);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    if (!accessToken) {
      setError("This reset link is invalid or has expired.");
      return;
    }
    try {
      await resetMutation.mutateAsync({ accessToken, newPassword: password });
      setDone(true);
    } catch (err) {
      const detail = (err as { body?: { detail?: string } } | undefined)?.body?.detail;
      logger.debug("[reset-password] failed:", err);
      setError(detail ?? "Couldn't reset your password. Please request a new link.");
    }
  };

  if (done) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center px-4">
        <Card className="w-full max-w-md border-0 shadow-2xl">
          <CardHeader className="text-center">
            <div className="bg-accent/10 mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full">
              <CheckCircle2 className="text-accent h-8 w-8" />
            </div>
            <CardTitle className="font-heading text-primary text-2xl font-bold">
              Password updated
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-muted mb-6">
              Your password has been reset. You can now sign in with your new password.
            </p>
            <Button
              onClick={() => navigate("/login")}
              className="bg-primary text-primary-foreground hover:bg-primary/90 w-full"
            >
              Go to sign in
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="bg-background flex min-h-screen items-center justify-center px-4">
      <Card className="w-full max-w-md border-0 shadow-2xl">
        <CardHeader className="space-y-3 px-8 pt-8 text-center md:px-10 md:pt-10">
          <p className="text-primary text-sm font-semibold tracking-[0.24em] uppercase">
            {PROGRAM_NAME}
          </p>
          <CardTitle className="font-heading text-foreground text-2xl font-semibold">
            Choose a new password
          </CardTitle>
          <p className="text-muted text-sm">Enter a new password for your account.</p>
        </CardHeader>
        <CardContent className="px-8 pb-8 md:px-10">
          {!accessToken ? (
            <div className="space-y-4 text-center">
              <p className="text-destructive text-sm">
                This reset link is invalid or has expired. Please request a new one.
              </p>
              <Button asChild variant="outline" className="w-full">
                <Link to="/forgot-password">Request a new link</Link>
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="password" className="text-foreground text-sm font-medium">
                  New password
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    placeholder="Enter new password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-muted hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2 transition-colors"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <label htmlFor="confirm" className="text-foreground text-sm font-medium">
                  Confirm new password
                </label>
                <Input
                  id="confirm"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="Re-enter new password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                />
              </div>
              {error && <p className="text-destructive text-sm">{error}</p>}
              <Button
                type="submit"
                disabled={resetMutation.isPending}
                className="bg-primary text-primary-foreground hover:bg-primary/90 w-full"
              >
                {resetMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Updating…
                  </>
                ) : (
                  "Reset password"
                )}
              </Button>
              <p className="text-muted text-center text-sm">
                <Link to="/login" className="text-accent hover:underline">
                  Back to sign in
                </Link>
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
